import { useCallback, useState, type ChangeEvent } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import type { ImagePreviews } from "./printLayoutForm";

export type ImageKind = "background" | "header" | "footer";
type Files = Record<ImageKind, File | null>;

const EMPTY_FILES: Files = { background: null, header: null, footer: null };
const EMPTY_PREVIEWS: ImagePreviews = { background: null, header: null, footer: null };
const MAX_BYTES = 5 * 1024 * 1024;
const BUCKET = "print-backgrounds";

export type ImageUploader = ReturnType<typeof useImageUpload>;

export function useImageUpload() {
  const { t } = useTranslation("settings");
  const [files, setFiles] = useState<Files>(EMPTY_FILES);
  const [previews, setPreviews] = useState<ImagePreviews>(EMPTY_PREVIEWS);

  const select = useCallback(
    (kind: ImageKind, file: File | null | undefined) => {
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        toast.error(t("selectValidImage"));
        return;
      }
      if (file.size > MAX_BYTES) {
        toast.error(t("imageMaxSize"));
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFiles((cur) => ({ ...cur, [kind]: file }));
        setPreviews((cur) => ({ ...cur, [kind]: reader.result as string }));
      };
      reader.readAsDataURL(file);
    },
    [t],
  );

  const onInputChange = useCallback(
    (kind: ImageKind) => (event: ChangeEvent<HTMLInputElement>) => {
      select(kind, event.target.files?.[0]);
      event.target.value = "";
    },
    [select],
  );

  const clearPending = useCallback((kind: ImageKind) => {
    setFiles((cur) => ({ ...cur, [kind]: null }));
    setPreviews((cur) => ({ ...cur, [kind]: null }));
  }, []);

  const reset = useCallback(() => {
    setFiles(EMPTY_FILES);
    setPreviews(EMPTY_PREVIEWS);
  }, []);

  const uploadOne = async (file: File): Promise<string> => {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) throw new Error(t("userNotAuthenticated"));
    const fileExt = file.name.split(".").pop();
    const fileName = `${userData.user.id}/${Date.now()}.${fileExt}`;
    const { error } = await supabase.storage.from(BUCKET).upload(fileName, file, { cacheControl: "3600", upsert: false });
    if (error) throw error;
    return supabase.storage.from(BUCKET).getPublicUrl(fileName).data.publicUrl;
  };

  const uploadPending = async (): Promise<Partial<Record<ImageKind, string>>> => {
    const out: Partial<Record<ImageKind, string>> = {};
    for (const kind of ["background", "header", "footer"] as const) {
      const file = files[kind];
      if (file) out[kind] = await uploadOne(file);
    }
    return out;
  };

  return { previews, select, onInputChange, clearPending, reset, uploadPending };
}
